import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Image as ImageIcon, FileText, Calendar, BarChart2, User, BookOpen, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import Username, { getDisplayName } from '../Common/Username';

const ForwardModal = ({ isOpen, onClose, message, messages: messagesBatch }) => {
  const { t } = useTranslation('chat');
  const { friends, sendMessage, selectedFriendId, selectFriend } = useChat();
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [isForwarding, setIsForwarding] = useState(false);

  // Normalize: always work with an array
  const messageList = useMemo(() => {
    if (messagesBatch && messagesBatch.length > 0) return messagesBatch;
    if (message) return [message];
    return [];
  }, [message, messagesBatch]);

  // Filter out deleted friends and current selected friend
  const availableFriends = useMemo(() => {
    return friends.filter((friend) => {
      const friendId = friend.email || friend.id;
      return !friend.isDeleted && friendId !== selectedFriendId && friend.lastMessageTime !== null;
    });
  }, [friends, selectedFriendId]);

  // Get message preview text
  const messagePreview = useMemo(() => {
    if (messageList.length === 0) return '';
    if (messageList.length > 1) return t('forwardModal.messageCount', { count: messageList.length });
    const msg = messageList[0];
    if (msg.type === 'text') return msg.content || msg.text || '';
    if (msg.type === 'image') return msg.file_name || msg.content || t('forwardModal.typeFallback.photo');
    if (msg.type === 'file') return msg.file_name || msg.content || t('forwardModal.typeFallback.file');
    if (msg.type === 'note_share') return msg.note_title || t('forwardModal.typeFallback.note');
    if (msg.type === 'event_share' || msg.type === 'event_cancelled') return msg.event_title || t('forwardModal.typeFallback.event');
    if (msg.type === 'poll') return msg.poll_title || t('forwardModal.typeFallback.poll');
    if (msg.type === 'contact') return msg.contact_name || t('forwardModal.typeFallback.contact');
    return msg.content || '';
  }, [messageList, t]);

  // Get preview icon for message type
  const getPreviewIcon = (type) => {
    if (type === 'image') return <ImageIcon className="w-5 h-5 text-[var(--color-text-muted)]" />;
    if (type === 'file') return <FileText className="w-5 h-5 text-[var(--color-text-muted)]" />;
    if (type === 'note_share') return <BookOpen className="w-5 h-5 text-[#3d8ef0]" />;
    if (type === 'event_share' || type === 'event_cancelled') return <Calendar className="w-5 h-5 text-[#3d8ef0]" />;
    if (type === 'poll') return <BarChart2 className="w-5 h-5 text-[#3d8ef0]" />;
    if (type === 'contact') return <User className="w-5 h-5 text-[#3d8ef0]" />;
    return null;
  };

  const handleToggleFriend = (friendId) => {
    setSelectedFriends((prev) => {
      if (prev.includes(friendId)) {
        return prev.filter((id) => id !== friendId);
      } else {
        return [...prev, friendId];
      }
    });
  };

  const handleForward = async () => {
    if (selectedFriends.length === 0 || messageList.length === 0 || isForwarding) return;

    setIsForwarding(true);
    try {
      // Forward each message to all selected friends (in order)
      for (const msg of messageList) {
        const forwardedFrom = { messageId: msg.id };
        const forwardPromises = selectedFriends.map(async (friendId) => {
          if (msg.type === 'text') {
            await sendMessage(friendId, msg.content || msg.text || '', 'text', null, null, null, forwardedFrom);
          } else if (msg.type === 'image') {
            await sendMessage(friendId, msg.content || msg.file_name || t('forwardModal.typeFallback.photo'), 'image', msg.file_url || msg.fileUrl, msg.file_name || 'image', null, forwardedFrom);
          } else if (msg.type === 'file') {
            await sendMessage(friendId, msg.file_name || msg.content || t('forwardModal.typeFallback.file'), 'file', msg.file_url || msg.fileUrl, msg.file_name || 'dosya', null, forwardedFrom);
          } else if (msg.type === 'note_share') {
            await sendMessage(
              friendId,
              msg.content || `📄 ${msg.note_title || t('forwardModal.typeFallback.note')}`,
              'note_share',
              null, null, null, forwardedFrom,
              null, null, null,
              {
                note_id: msg.note_id,
                note_title: msg.note_title,
                note_content: msg.note_content,
                note_title_color: msg.note_title_color,
              }
            );
          } else if (msg.type === 'event_share') {
            await sendMessage(
              friendId,
              msg.content || `📅 ${msg.event_title || t('forwardModal.typeFallback.event')}`,
              'event_share',
              null, null, null, forwardedFrom,
              {
                event_id: msg.event_id,
                event_title: msg.event_title,
                event_start: msg.event_start,
                event_end: msg.event_end,
                event_all_day: msg.event_all_day,
                event_description: msg.event_description,
                event_location: msg.event_location,
              }
            );
          } else if (msg.type === 'contact') {
            await sendMessage(
              friendId,
              msg.content || `👤 ${msg.contact_name || t('forwardModal.typeFallback.contact')}`,
              'contact',
              null, null, null, forwardedFrom,
              null, null,
              {
                contact_user_id: msg.contact_user_id,
                contact_name: msg.contact_name,
                contact_avatar_url: msg.contact_avatar_url,
                contact_email: msg.contact_email,
              }
            );
          } else if (msg.type === 'poll') {
            // Polls can't be forwarded — options aren't stored in the message object
            window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
              detail: { type: 'warning', title: t('forwardModal.pollWarningTitle'), message: t('forwardModal.pollWarningMessage') },
            }));
          }
          // event_cancelled and unknown types are silently skipped
        });
        await Promise.all(forwardPromises);
      }

      // Close modal and reset state
      setSelectedFriends([]);
      onClose();

      // Navigate to first forwarded friend's chat after forwarding
      if (selectedFriends.length > 0 && selectFriend) {
        selectFriend(selectedFriends[0]);
      }
    } catch (error) {
      console.error('Forward message error:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: { type: 'error', title: t('common:errorTitle'), message: t('forwardModal.forwardError') },
        })
      );
    } finally {
      setIsForwarding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-md z-[10000]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 flex items-center justify-center p-4 z-[10001]"
            onClick={onClose}
          >
            <div
              className={`bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md border border-white/10 dark:border-white/10 max-h-[85vh] flex flex-col overflow-hidden`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10 dark:border-white/10">
                <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white">
                  {t('forwardModal.title')}
                </h3>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Message Preview */}
              {messageList.length > 0 && (
                <div className="p-4 bg-[var(--color-bg-muted)] dark:bg-[#161f31] border-b border-white/10 dark:border-white/10">
                  <div className="flex items-start gap-3">
                    {messageList.length > 1 ? (
                      <div className="w-10 h-10 rounded-lg bg-[#3d8ef0]/20 flex items-center justify-center flex-shrink-0">
                        <ArrowRight className="w-5 h-5 text-[#3d8ef0]" />
                      </div>
                    ) : getPreviewIcon(messageList[0]?.type) ? (
                      <div className="w-10 h-10 rounded-lg bg-[#24354f] flex items-center justify-center flex-shrink-0">
                        {getPreviewIcon(messageList[0]?.type)}
                      </div>
                    ) : null}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white truncate">
                        {messageList.length > 1 ? t('forwardModal.messagesToForward', { count: messageList.length }) : (messagePreview.length > 50 ? messagePreview.substring(0, 50) + '...' : messagePreview)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Friends List */}
              <div className="flex-1 overflow-y-auto p-4">
                {availableFriends.length === 0 ? (
                  <div className="text-center py-8 text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]">
                    <p>{t('forwardModal.noContacts')}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availableFriends.map((friend) => {
                      const friendId = friend.email || friend.id;
                      const isSelected = selectedFriends.includes(friendId);
                      
                      return (
                        <button
                          key={friendId}
                          onClick={() => handleToggleFriend(friendId)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                            isSelected
                              ? 'bg-[#2563eb]/10 dark:bg-[#2563eb]/10 border-2 border-[#3d8ef0]/50 dark:border-[#3d8ef0]/50'
                              : 'bg-[var(--color-bg-muted)] dark:bg-[#161f31] border-2 border-transparent hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47]'
                          }`}
                        >
                          {/* Avatar */}
                          <div className="w-10 h-10 rounded-full bg-[#2563eb]/10 dark:bg-[#2563eb]/10 flex items-center justify-center text-sm font-semibold text-[#3d8ef0] dark:text-[#7ab8f8] flex-shrink-0">
                            {friend.avatarUrl ? (
                              <img
                                src={friend.avatarUrl}
                                alt={getDisplayName(friend)}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              getDisplayName(friend).charAt(0).toUpperCase()
                            )}
                          </div>
                          
                          {/* Name */}
                          <div className="flex-1 text-left min-w-0">
                            <p className={`text-sm truncate ${
                              isSelected
                                ? 'text-[#3d8ef0] dark:text-primary-300'
                                : ''
                            }`}>
                              <Username user={friend} variant="default" />
                            </p>
                          </div>
                          
                          {/* Check Icon */}
                          {isSelected && (
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#2563eb] flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/10 dark:border-white/10 flex items-center justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] rounded-lg transition-colors"
                  disabled={isForwarding}
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleForward}
                  disabled={selectedFriends.length === 0 || isForwarding}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
                    selectedFriends.length > 0 && !isForwarding
                      ? 'bg-[#2563eb] hover:bg-[#3d8ef0] text-white shadow-lg shadow-primary-600/30'
                      : 'bg-gray-300 dark:bg-[#161f31] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] cursor-not-allowed'
                  }`}
                >
                  {isForwarding ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('forwardModal.forwarding')}
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4" />
                      {t('forwardModal.forwardButton', { count: selectedFriends.length })}
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ForwardModal;
